<template>
  <div class="service-plans">
    <v-data-table
      :headers="headers"
      :items="servicePlans"
      show-expand
      single-expand
      :expanded.sync="expanded"
      item-key="servicePlanId"
      :item-class="highlightExpandedRow"
      :items-per-page="-1"
      hide-default-footer
      class="primary--text"
      :key="servicePlans.length"
      :mobile-breakpoint="mobileBreakpoint"
      @click:row="rowClicked"
      :sort-by="['isNew', 'program', 'service', 'startDate']"
      :sort-desc="[false, false, false, true]"
    >
      <!-- Program -->
      <template v-slot:[`item.program`]="{ item }">
        <template v-if="!editMode">
          {{ item.program }}
          <v-chip
            v-if="item.isCompensatorySummer"
            x-small
            pill
            label
            color="accent"
            class="ma-1"
            >Compensatory</v-chip
          >
          <v-tooltip bottom :disabled="!item.isPartialMandate">
            <template
              v-if="item.isAssignedToOmni"
              v-slot:activator="{ on }"
            >
              <v-chip v-on="on" x-small pill label color="accent" class="ma-1">
                {{
                  item.isPartialMandate
                    ? "Partially Assigned To Omni"
                    : "Assigned to Omni"
                }}
              </v-chip>
            </template>
            <span v-if="item.isPartialMandate">{{
              item.transmittalMandate
            }}</span>
          </v-tooltip>
        </template>
        <validation-provider v-else rules="required" v-slot="{ errors }">
          <v-autocomplete
            v-model="item.programId"
            :items="programs"
            :item-text="(p) => `${p.code} - ${p.name}`"
            item-value="programId"
            placeholder="Program"
            dense
            hide-details="auto"
            :error-messages="errors"
            clearable
            @change="programChanged(item)"
          ></v-autocomplete>
        </validation-provider>
      </template>

      <!-- Service -->
      <template v-slot:[`item.service`]="{ item }">
        <template v-if="!editMode">{{ item.service }}</template>
        <validation-provider v-else rules="required" v-slot="{ errors }">
          <v-autocomplete
            v-model="item.serviceId"
            :items="servicesForProgram(item.programId)"
            :item-text="(s) => `${s.code} - ${s.name}`"
            item-value="serviceId"
            placeholder="Service"
            dense
            hide-details="auto"
            :error-messages="errors"
            clearable
          ></v-autocomplete>
        </validation-provider>
      </template>

      <!-- Mandate -->
      <template v-slot:[`item.mandate`]="{ item }">
        <template v-if="!editMode">{{ item.mandate }}</template>
        <div v-else class="d-flex align-center" style="gap: 2px">
          <validation-provider :rules="{ required: true }" v-slot="{ errors }">
            <v-select
              v-model="item.frequency"
              :items="frequencies"
              placeholder="Fr"
              dense
              hide-details
              :error="errors.length > 0"
              style="width: 52px"
            ></v-select>
          </validation-provider>
          <span class="grey--text">×</span>
          <validation-provider :rules="{ required: true }" v-slot="{ errors }">
            <v-select
              v-model="item.durationMinutes"
              :items="durations"
              placeholder="Dur"
              dense
              hide-details
              :error="errors.length > 0"
              style="width: 52px"
            ></v-select>
          </validation-provider>
          <span class="grey--text">:</span>
          <validation-provider :rules="{ required: true }" v-slot="{ errors }">
            <v-text-field
              v-model.number="item.groupSize"
              type="number"
              min="1"
              max="10"
              placeholder="Gr"
              dense
              hide-details
              :error="errors.length > 0"
              style="width: 40px"
            ></v-text-field>
          </validation-provider>
        </div>
      </template>

      <!-- Location -->
      <template v-slot:[`item.sessionLocation`]="{ item }">
        <template v-if="!editMode">{{ item.sessionLocation }}</template>
        <validation-provider
          v-else
          :rules="{ required: true }"
          v-slot="{ errors }"
        >
          <v-autocomplete
            v-model="item.sessionLocationId"
            :items="locations"
            :item-text="(l) => `${l.legacyLocationId} - ${l.name}`"
            item-value="sessionLocationId"
            placeholder="Location"
            dense
            hide-details="auto"
            :error-messages="errors"
            clearable
          ></v-autocomplete>
        </validation-provider>
      </template>

      <!-- Auth start -->
      <template v-slot:[`item.formattedStartDate`]="{ item }">
        <template v-if="!editMode">{{ item.formattedStartDate }}</template>
        <date-picker
          v-else
          :field="{ label: '' }"
          :value="item.startDate"
          :validations="{
            required: true,
            minDate: plan.planStartDate,
            maxDate: plan.closureDate,
          }"
          @fieldChange="item.startDate = $event"
        ></date-picker>
      </template>

      <!-- Auth end -->
      <template v-slot:[`item.formattedEndDate`]="{ item }">
        <template v-if="!editMode">{{ item.formattedEndDate }}</template>
        <date-picker
          v-else
          :field="{ label: '' }"
          :value="item.endDate"
          :validations="{
            required: true,
            minDate: item.startDate,
            maxDate: plan.closureDate,
            maxAgingOutDate: agingOutDate,
          }"
          @fieldChange="item.endDate = $event"
        ></date-picker>
      </template>

      <!-- More options (both modes, read-only when viewing) + remove (edit) -->
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex align-center justify-end flex-nowrap">
          <service-plan-options
            :servicePlan="item"
            :editMode="editMode"
          ></service-plan-options>
          <v-tooltip v-if="editMode" bottom :disabled="canRemove(item)">
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <v-icon
                  small
                  color="primary"
                  :disabled="!canRemove(item)"
                  @click="requestRemove(item)"
                  >mdi-delete</v-icon
                >
              </span>
            </template>
            <span>Cannot remove a service plan with assignments</span>
          </v-tooltip>
        </div>
      </template>

      <!-- Expanded row: provider assignments (both modes) -->
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="expandedRow">
          <template v-if="item.servicePlanId > 0">
            <provider-assignments-table
              :servicePlanId="item.servicePlanId"
              :viewOnly="!canEditAssignments"
            ></provider-assignments-table>
            <v-card-actions class="justify-center white mb-3 py-3">
              <provider-assignment-form
                v-if="canEditAssignments && item.canCreateAssignments"
                :servicePlanId="item.servicePlanId"
              ></provider-assignment-form>
            </v-card-actions>
          </template>
          <div v-else class="text-caption font-italic grey--text pa-3 white">
            Save the plan to manage provider assignments for this service.
          </div>
        </td>
      </template>

      <!-- Add row (edit) -->
      <template v-if="editMode" v-slot:[`footer`]>
        <v-card-actions class="justify-center">
          <v-btn rounded outlined small color="primary" @click="$emit('add')">
            <v-icon small color="primary">mdi-plus</v-icon> New Service Plan
          </v-btn>
        </v-card-actions>
      </template>
    </v-data-table>
    <confirm-modal ref="confirmRemove"></confirm-modal>
  </div>
</template>

<script>
import ProviderAssignmentsTable from "./provider-assignments-table";
import ProviderAssignmentForm from "./provider-assignment-form";
import ServicePlanOptions from "./service-plan-options";
import ConfirmModal from "../../Shared/confirm-modal";
export default {
  components: {
    ProviderAssignmentsTable,
    ProviderAssignmentForm,
    ServicePlanOptions,
    ConfirmModal,
  },
  props: ["plan", "servicePlans", "editMode", "canEditAssignments", "agingOutDate"],
  data() {
    return {
      headers: [
        { text: "Program", value: "program" },
        { text: "Service", value: "service" },
        { text: "Mandate", value: "mandate" },
        { text: "Location", value: "sessionLocation" },
        { text: "Auth Start Date", value: "formattedStartDate" },
        { text: "Auth End Date", value: "formattedEndDate" },
        { text: "Options", value: "actions", sortable: false, align: "end" },
      ],
      expanded: [],
      mobileBreakpoint: 1264, //px for "md" device
    };
  },
  computed: {
    frequencies() {
      return this.$store.state.optionsModule.frequencies || [];
    },
    durations() {
      return this.$store.state.optionsModule.durations || [];
    },
    programs() {
      return (
        this.$store.state.optionsModule.programs
          .slice()
          .sort((a, b) => a.code.localeCompare(b.code)) || []
      );
    },
    locations() {
      return (
        this.$store.state.optionsModule.sessionLocations
          .slice()
          .sort((a, b) =>
            a.legacyLocationId.localeCompare(b.legacyLocationId)
          ) || []
      );
    },
  },
  methods: {
    rowClicked(row, { isExpanded }) {
      if (this.editMode) return;
      this.expanded = isExpanded ? [] : [row];
    },

    highlightExpandedRow(row) {
      if (this.editMode) return "";
      return this.expanded.includes(row)
        ? "clickable-row expandedRow"
        : "clickable-row";
    },
    servicesForProgram(programId) {
      return (
        this.$store.getters["optionsModule/getServicesByProgram"][programId] ||
        []
      );
    },
    // the service list is program-specific, so clear service on program change
    programChanged(item) {
      item.serviceId = null;
    },
    canRemove(sp) {
      return (
        sp.servicePlanId <= 0 || !(sp.assignments && sp.assignments.length)
      );
    },
    async requestRemove(item) {
      // need to confirm existing service plan will be deleted when plan is saved
      var confirmed = false;
      if (item.servicePlanId > 0) {
        confirmed = await this.$refs.confirmRemove.open(
          "Are you sure you want to REMOVE this service plan?",
          "It will be permanently deleted when you save the plan. This action cannot be undone.",
          "Remove"
        );
      }

      if (confirmed || item.servicePlanId <= 0) {
        this.$emit("remove", item);
      }
    },
  },
};
</script>
<style>
.service-plans .v-data-table-header-mobile {
  display: none;
}
.service-plans
  .v-data-table--mobile
  > .v-data-table__wrapper
  > table
  > tbody
  > tr.v-data-table__expanded__content
  > td {
  display: block;
  height: auto;
  padding: 20px;
}
.expandedRow {
  background: #f5f7f8;
}
.clickable-row {
  cursor: pointer;
}
</style>
